/**
 * AdminDocsPage — internal knowledge-base viewer for full admins +
 * sub-admins. Mirrors the /help/docs layout: search bar, category cards,
 * and collapsible article accordions. Capability chips are not shown
 * (the server already filters articles by the caller's role/capabilities).
 */
import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import Layout from '../../components/layout/Layout';
import { Loader2, LifeBuoy, BookOpen, ChevronDown, ChevronUp } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../components/ui/collapsible';
import HelpBrowseShell, { matchesSearch } from '../../components/common/HelpBrowseShell';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminDocsPage = () => {
  const [loading, setLoading] = useState(true);
  const [sections, setSections] = useState([]);
  const [categories, setCategories] = useState([]);
  const [openSections, setOpenSections] = useState(new Set());
  const [search, setSearch] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const [secRes, catRes] = await Promise.all([
          axios.get(`${API}/admin/docs/sections`),
          axios.get(`${API}/admin/docs/categories`),
        ]);
        setSections(secRes.data || []);
        setCategories(catRes.data || []);
        if ((secRes.data || []).length > 0) {
          setOpenSections(new Set([secRes.data[0].id]));
        }
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to load docs');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filteredSections = useMemo(
    () => sections.filter((s) => matchesSearch(s, search)),
    [sections, search],
  );

  const grouped = useMemo(() => {
    const byCat = new Map();
    filteredSections.forEach((s) => {
      const key = s.category_id || '__uncat__';
      if (!byCat.has(key)) byCat.set(key, []);
      byCat.get(key).push(s);
    });
    const catList = categories
      .filter((c) => byCat.has(c.id))
      .map((c) => ({ ...c, sections: byCat.get(c.id) }));
    const uncat = byCat.get('__uncat__') || [];
    return { catList, uncat };
  }, [filteredSections, categories]);

  const categoriesWithCount = useMemo(() => {
    const counts = new Map();
    sections.forEach((s) => {
      const key = s.category_id || null;
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return categories.map((c) => ({ ...c, article_count: counts.get(c.id) || 0 }));
  }, [sections, categories]);

  const uncategorisedCount = useMemo(
    () => sections.filter((s) => !s.category_id).length,
    [sections],
  );

  const toggleSection = (id) => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const scrollToCategory = (categoryId) => {
    const el = document.getElementById(categoryId ? `cat-${categoryId}` : 'cat-uncategorised');
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-slate-500 mr-2" />
          <span className="text-slate-500">Loading docs…</span>
        </div>
      </Layout>
    );
  }

  const totalHits = filteredSections.length;
  const hasContent = totalHits > 0;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        <HelpBrowseShell
          title="Admin Docs"
          subtitle="Internal how-to guides for the admin team."
          categories={categoriesWithCount}
          uncategorisedCount={uncategorisedCount}
          search={search}
          onSearchChange={setSearch}
          resultsCount={search ? totalHits : null}
          onCategoryClick={scrollToCategory}
        />

        {hasContent ? (
          <div className="space-y-8" data-testid="admin-docs-article-list">
            {grouped.catList.map((cat) => (
              <section key={cat.id} id={`cat-${cat.id}`} className="scroll-mt-20">
                <h2 className="text-lg font-semibold text-slate-800 mb-3 flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-emerald-600" />
                  {cat.name}
                  <span className="text-xs font-normal text-slate-400">· {cat.sections.length}</span>
                </h2>
                <div className="space-y-2">
                  {cat.sections.map((s) => (
                    <ArticleAccordion
                      key={s.id}
                      article={s}
                      open={openSections.has(s.id)}
                      onToggle={() => toggleSection(s.id)}
                    />
                  ))}
                </div>
              </section>
            ))}
            {grouped.uncat.length > 0 && (
              <section id="cat-uncategorised" className="scroll-mt-20">
                <h2 className="text-lg font-semibold text-slate-800 mb-3 flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-slate-400" />
                  Other
                  <span className="text-xs font-normal text-slate-400">· {grouped.uncat.length}</span>
                </h2>
                <div className="space-y-2">
                  {grouped.uncat.map((s) => (
                    <ArticleAccordion
                      key={s.id}
                      article={s}
                      open={openSections.has(s.id)}
                      onToggle={() => toggleSection(s.id)}
                    />
                  ))}
                </div>
              </section>
            )}
          </div>
        ) : (
          <div
            className="text-center py-12 border rounded-lg bg-slate-50/50"
            data-testid="admin-docs-empty-state"
          >
            <LifeBuoy className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            {search ? (
              <>
                <p className="text-slate-700 font-medium">No results for &ldquo;{search}&rdquo;</p>
                <p className="text-sm text-slate-500 mt-1">Try a different search term.</p>
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="mt-3 text-sm text-emerald-600 hover:underline"
                  data-testid="admin-docs-clear-search"
                >
                  Clear search
                </button>
              </>
            ) : (
              <>
                <p className="text-slate-700 font-medium">No docs yet</p>
                <p className="text-xs text-slate-500 mt-1">
                  Author articles from{' '}
                  <code className="bg-slate-100 px-1 rounded">/admin/tools/docs-manager</code>.
                </p>
              </>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

const ArticleAccordion = ({ article, open, onToggle }) => (
  <div
    className="border rounded-lg bg-white"
    id={`section-${article.id}`}
    data-testid={`docs-article-${article.id}`}
  >
    <Collapsible open={open} onOpenChange={onToggle}>
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-50 rounded-lg transition"
          data-testid={`docs-article-toggle-${article.id}`}
        >
          <span className="font-medium text-slate-900">{article.title}</span>
          {open
            ? <ChevronUp className="w-5 h-5 text-slate-500 flex-shrink-0" />
            : <ChevronDown className="w-5 h-5 text-slate-500 flex-shrink-0" />}
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        {article.content && (
          <div
            className="px-4 pb-4 pt-1 border-l-2 border-emerald-500/20 ml-4 user-guide-content"
            dangerouslySetInnerHTML={{ __html: (article.content || '').replace(/&nbsp;/g, ' ') }}
          />
        )}
        {(article.video_embeds || []).length > 0 && (
          <div className="border-l-2 border-emerald-500/20 ml-4 pl-4 pr-4 pt-2 pb-4 space-y-4">
            {article.video_embeds.map((v, i) => (
              <div key={v.id || i} className="space-y-1">
                {v.title && <p className="text-sm font-medium text-slate-700">{v.title}</p>}
                <div className="relative rounded-lg overflow-hidden bg-black" style={{ paddingBottom: '56.25%' }}>
                  <iframe
                    src={v.url}
                    title={v.title || `Video ${i + 1}`}
                    className="absolute inset-0 w-full h-full border-0"
                    loading="lazy"
                    allow="fullscreen; autoplay; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </CollapsibleContent>
    </Collapsible>
  </div>
);

export default AdminDocsPage;
